import type { ProductType, ProductInstance } from './company.types';

export interface ProductInfo {
  type: ProductType;
  displayName: string;
  description: string;
  category: 'financial' | 'hr' | 'project' | 'operations';
  requiresAutoConnect?: ProductType[];
}

export const PRODUCT_CATALOG: Record<ProductType, ProductInfo> = {
  'access-financials': {
    type: 'access-financials',
    displayName: 'Access Financials',
    description: 'Financial management and accounting system',
    category: 'financial',
  },
  'access-financials-operations': {
    type: 'access-financials-operations',
    displayName: 'Access Financials Operations',
    description: 'Operations management with auto-connection to Financials and Resource Scheduler',
    category: 'operations',
    requiresAutoConnect: ['access-financials', 'access-resource-scheduler'],
  },
  'access-dimensions': {
    type: 'access-dimensions',
    displayName: 'Access Dimensions',
    description: 'Multi-dimensional analysis and reporting',
    category: 'financial',
  },
  'access-focalpoint': {
    type: 'access-focalpoint',
    displayName: 'Access FocalPoint',
    description: 'Business intelligence and analytics platform',
    category: 'financial',
  },
  'people-hr': {
    type: 'people-hr',
    displayName: 'People HR',
    description: 'Human resources management system',
    category: 'hr',
  },
  'access-projects': {
    type: 'access-projects',
    displayName: 'Access Projects',
    description: 'Project management and tracking',
    category: 'project',
  },
  'access-resource-scheduler': {
    type: 'access-resource-scheduler',
    displayName: 'Access Resource Scheduler',
    description: 'Resource planning and scheduling',
    category: 'operations',
  },
};

export function getProductDisplayName(productType: ProductType): string {
  return PRODUCT_CATALOG[productType]?.displayName || productType;
}

export function getProductInfo(productType: ProductType): ProductInfo | undefined {
  return PRODUCT_CATALOG[productType];
}

export function generateMockInstanceName(productType: ProductType, companyCode: string): string {
  const prefix = companyCode.toUpperCase();
  const productAbbrev = productType
    .split('-')
    .map(word => word.charAt(0).toUpperCase())
    .join('');
  
  return `${prefix}-${productAbbrev}-${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
}

export function shouldAutoConnect(productType: ProductType): ProductType[] {
  return PRODUCT_CATALOG[productType]?.requiresAutoConnect || [];
}

export function createAutoConnectedInstance(
  baseProductId: string,
  productType: ProductType,
  companyId: string,
  companyCode: string
): ProductInstance {
  return {
    id: `auto-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    companyId,
    productType,
    instanceName: generateMockInstanceName(productType, companyCode),
    connectionStatus: 'connected',
    apiEndpoint: null,
    apiKey: null,
    lastSync: new Date(),
    lastTestDate: new Date(),
    lastTestStatus: 'success',
    syncFrequency: 'daily',
    autoSync: true,
    config: {},
    errorMessage: null,
    autoConnectedBy: baseProductId,
  };
}

export function getAvailableProducts(existingInstances: ProductInstance[]): ProductType[] {
  const existingTypes = new Set(existingInstances.map(i => i.productType));
  return Object.keys(PRODUCT_CATALOG).filter(
    type => !existingTypes.has(type as ProductType)
  ) as ProductType[];
}
